import React, { Component, Fragment } from "react";
import PropTypes from "prop-types";
import { Tab, Nav, NavItem } from "react-bootstrap";
import { SubNavBar } from "@components/common";
import { ClientService, OrganizationService } from "@services";
import utils from "@utils";
import { DetailPanel, ActivityTab, LocationsTab, ContactsTab } from "./components";
import { Loader, ConfirmationModal } from "../../../components";
import { InviteClientModal } from "../components";

import "./ClientView.scss";

class ClientView extends Component {
	constructor(props) {
		super(props);

		this.state = {
			activeTab: "Activities",
			showInviteClientModal: false,
			client: {
				data: {},
				loading: false,
			},
			coordinatorFee: {
				value: 0,
				loading: false,
			},
			contacts: {
				data: [],
				pageNumber: 1,
				hasMore: true,
			},
		};
	}

	componentDidMount = () => {
		const { user } = this.props;
		if (!user.isCoordinator()) window.location.href = "/";
		this.getClient();
	};

	/** Retrieves client details along with stats shown to coordinators */
	getClient = () => {
		const { lang } = global;
		const {
			router: {
				params: { clientId },
			},
		} = this.props;

		if (!clientId) return;

		this.setState(state => ({ client: { ...state.client, loading: true } }));

		ClientService.getById(clientId, { includeStats: true })
			.then(({ data }) => {
				this.setState({
					client: { data, loading: false },
					coordinatorFee: { value: data.OrganizationFee, loading: false },
				});
			})
			.catch(() => {
				this.setState(state => ({ client: { ...state.client, loading: false } }));

				utils.notify({
					type: "error",
					message: lang.t("Error getting Client Details"),
				});
			});
	};

	/** Updates the organization fee for the client */
	saveOrganizationFee = () => {
		const { lang } = global;
		const { coordinatorFee } = this.state;
		const {
			router: {
				params: { clientId },
			},
		} = this.props;

		if (coordinatorFee < 0 || coordinatorFee.value === "") {
			utils.notify({
				type: "error",
				message: lang.t("Error updating Coordination Fee"),
				title: lang.t("Error"),
			});
			return;
		}

		const organizationFeeModelList = [
			{
				OrganizationFee: coordinatorFee.value,
				ClientId: clientId,
				UpdateAllLocationFee: false,
				LocationFeeModelList: [],
			},
		];

		this.setState(state => ({ coordinatorFee: { ...state.coordinatorFee, loading: true } }));

		OrganizationService.updateOrganizationFees({ organizationFeeModelList })
			.then(() => {
				utils.notify({
					type: "success",
					message: lang.t("Coordination Fee updated successfully"),
					title: lang.t("Success"),
				});

				this.setState(
					state => ({
						coordinatorFee: { ...state.coordinatorFee, loading: false },
					}),
					this.getClient,
				);
			})
			.catch(() => {
				utils.notify({
					type: "error",
					message: lang.t("Error updating Coordination Fee"),
					title: lang.t("Error"),
				});

				this.setState(state => ({
					coordinatorFee: { ...state.coordinatorFee, loading: false },
				}));
			});
	};

	/**
	 * Prompts the user with a confirmation modal when they choose to delete a client
	 * @param {Event} event the HTML event handled by this method
	 */
	deleteClient = event => {
		if (event) event.preventDefault();

		const { lang } = global;

		this._confirmationModal.openModal({
			title: lang.t("Delete Client"),
			message: `${lang.t("Are you sure you want to delete this client_")}
						${lang.t("All projects and jobs related to this client will be deleted_")}
						${lang.t("This action cannot be undone_")}`,
			confirmButton: `${lang.t("Yes")}, ${lang.t("Delete Client")}`,
			cancelButton: `${lang.t("No")}, ${lang.t("Keep this client_")}`,
			onConfirm: () => {
				const {
					client: {
						data: { Id },
					},
				} = this.state;
				const { router } = this.props;

				this.setState(state => ({ client: { ...state.client, loading: true } }));

				ClientService.delete(Id)
					.then(() => {
						utils.notify({
							type: "success",
							message: lang.t("Client deleted successfully"),
							title: lang.t("Success"),
						});

						router.push("/clients");
					})
					.catch(() => {
						this.setState(state => ({ client: { ...state.client, loading: false } }));

						utils.notify({
							type: "error",
							message: lang.t("Error deleting client"),
						});
					});
			},
		});
	};

	toggleInviteClientModal = () => {
		this.setState(prevState => ({ showInviteClientModal: !prevState.showInviteClientModal }));
	};

	updateContacts = (props, callback) => {
		this.setState(
			state => ({
				contacts: { ...state.contacts, ...props },
			}),
			() => {
				if (callback) callback();
			},
		);
	};

	/**
	 * Updates the value of coordinator fee
	 * @param {Event} event The HTML event handled by this method
	 */
	onInputChange = event => {
		const { value } = event.target;

		// Only allow digits, one dot and two decimals
		if (value.match(/^\d*\.?\d{0,2}$/g)) {
			this.setState(state => ({
				coordinatorFee: {
					...state.coordinatorFee,
					value,
				},
			}));
		}
	};

	onClickBack = () => {
		const { router } = this.props;
		router.push("/clients");
	};

	render() {
		const { lang } = global;
		const {
			router: {
				params: { clientId },
				push,
			},
			user,
		} = this.props;
		const { client, coordinatorFee, activeTab, contacts, showInviteClientModal } = this.state;
		const {
			data: { IsAgencyRKPayEnabled },
		} = client;

		return (
			<Fragment>
				<SubNavBar
					backText={lang.t("Back to clients")}
					onClickBack={this.onClickBack}
				/>
				<div className="container">
					<div
						className="row"
						id="client-view-header"
					>
						<div
							className="col-md-4 col-sm-12"
							id="client-view-title"
						>
							{lang.t("Client Profile")}
						</div>
						<If condition={IsAgencyRKPayEnabled}>
							<div className="col-md-4 col-sm-12">
								<div id="client-view-header-fees">
									<strong>{lang.t("Coordination Fees")}</strong>
									&nbsp;
									<i className="rkf rkf-dollar" />
									<input
										type="tel"
										name="OrganizationFee"
										min="0"
										placeholder="0.00"
										value={coordinatorFee.value}
										className="form-control"
										onChange={this.onInputChange}
									/>
									&nbsp;/hr
									<If
										condition={
											coordinatorFee.value !== client.data.OrganizationFee
										}
									>
										<Choose>
											<When condition={!coordinatorFee.loading}>
												<button
													type="button"
													id="save-coordination-fee"
													className="btn btn-primary"
													onClick={this.saveOrganizationFee}
												>
													{lang.t("Save")}
												</button>
											</When>
											<When condition={coordinatorFee.loading}>
												<Loader
													active
													spinnerOnly
													size="small"
												/>
											</When>
										</Choose>
									</If>
								</div>
								<small>
									{`${lang.t("Note that all locations will inherit this rate_")}
									  ${lang.t("You can override by setting rates per location_")}`}
								</small>
							</div>
						</If>
						<div
							className={`col-xs-12 ${IsAgencyRKPayEnabled ? "col-md-4" : "col-md-8"}`}
							id="client-view-header-btn-container"
						>
							<button
								type="button"
								className="btn btn-primary"
								name="invite-to-rollkall"
								onClick={this.toggleInviteClientModal}
							>
								{`+ ${lang.t("Invite to RollKall")}`}
							</button>
							<button
								type="button"
								className="btn btn-primary"
								onClick={() => push(`clients/form/${clientId}`)}
								name="edit-client"
								title={lang.t("Edit Client")}
								disabled={client.loading}
							>
								{lang.t("Edit Client")}
							</button>
							<button
								type="button"
								className="btn btn-danger"
								onClick={this.deleteClient}
								name="delete-client"
								title={lang.t("Delete Client")}
								disabled={client.loading}
							>
								{lang.t("Delete Client")}
							</button>
						</div>
					</div>

					<div className="row">
						<div className="col-md-4">
							<DetailPanel
								client={client.data}
								getClient={this.getClient}
							/>
						</div>
						<div className="col-md-8">
							<Tab.Container
								id="tabs"
								activeKey={activeTab}
								onSelect={e => this.setState({ activeTab: e })}
							>
								<div className="client-view-tabs-container">
									<Nav bsStyle="tabs">
										<NavItem eventKey="activities">
											{lang.t("Activities")}
										</NavItem>
										<NavItem eventKey="locations">
											{lang.t("Locations")}
										</NavItem>
										<NavItem eventKey="contacts">
											{lang.t("Client Contacts")}
										</NavItem>
									</Nav>

									<Tab.Content animation>
										<If condition={client.data?.Id}>
											<Tab.Pane eventKey="activities">
												<ActivityTab
													clientId={clientId}
													user={user}
												/>
											</Tab.Pane>
											<Tab.Pane eventKey="locations">
												<LocationsTab
													clientId={clientId}
													user={user}
													contacts={contacts.data}
													onEditLocation={this.onEditLocation}
													coordinatorFee={coordinatorFee.value}
												/>
											</Tab.Pane>
											<Tab.Pane eventKey="contacts">
												<ContactsTab
													client={client.data}
													contacts={contacts}
													getClient={this.getClient}
													updateContacts={this.updateContacts}
												/>
											</Tab.Pane>
										</If>
									</Tab.Content>
								</div>
							</Tab.Container>
						</div>
					</div>
				</div>
				<InviteClientModal
					show={showInviteClientModal}
					clientId={clientId}
					organizationFee={client.data.OrganizationFee}
					onHide={this.toggleInviteClientModal}
					onSuccess={this.toggleInviteClientModal}
				/>
				<ConfirmationModal
					ref={ref => {
						this._confirmationModal = ref;
					}}
				/>
			</Fragment>
		);
	}
}

ClientView.propTypes = {
	user: PropTypes.object,
	router: PropTypes.object.isRequired,
};

ClientView.defaultProps = {
	user: {},
};

export default ClientView;
